import { pipToDecimal } from '@katanaperps/katana-perps-sdk';
import { skipToken, useQuery } from '@tanstack/react-query';
import { isCallException } from 'ethers';

import { ApiVault, ApiVaultType } from '#/components/vaults/shared/types';
import { sharesToQuantityPips } from '#/components/vaults/shared/vaultShares';
import {
  FixedIncomeVaultTransformedConfig,
  ProfitShareVaultTransformedConfig,
  transformConfigurationFields,
  transformProfitShareConfigurationFields,
  VaultTransformedConfig,
} from '#/lib/ethereum/contracts/vaults/vaultsConfigContracts';
import {
  getExchangeContract,
  getFixedIncomeVaultAggregatorContract,
  getFixedIncomeVaultProviderContract,
  getProfitShareVaultAggregatorContract,
  getProfitShareVaultProviderContract,
  getVaultProviderContract,
} from '#/lib/ethereum/contracts/vaults/vaultsContract';
import { UnreachableCaseError } from '#/lib/utils';

import { minutes, seconds } from './utils';
import { useVaultQuery } from './vaultsQueries';

/**
 * Reads go through the vault's own provider / aggregator contracts (vaults
 * API `type`, `provider`, `stateAggregator`), so queries skip until the vault
 * is loaded and carry those fields in the queryKey.
 * Results are normalized: Profit Share share balances are exposed as their
 * USDC value (`shares * sharePrice`) under the same `owedQuantity` /
 * `grossQuantity` names Fixed Income uses.
 */

/** Key segment identifying the provider contracts a query reads from */
const queryKeyForAggregator = (vault: ApiVault | undefined) => [
  vault?.type,
  vault?.provider,
  vault?.stateAggregator,
];

export function useVaultOnChainConfigurationsQuery({
  managerWallet,
}: {
  managerWallet: string;
}) {
  const { data: vault } = useVaultQuery({ managerWallet });
  return useQuery<VaultTransformedConfig[]>({
    queryKey: [
      'vaultsOnChain',
      'configurations',
      managerWallet,
      ...queryKeyForAggregator(vault),
    ],
    queryFn: !vault
      ? skipToken
      : async () => {
          switch (vault.type) {
            case 'fixedIncomeVaultV1': {
              if (!vault.stateAggregator) {
                return [];
              }
              const configurations =
                await getFixedIncomeVaultAggregatorContract(
                  vault.stateAggregator
                ).loadVaultConfigurations(managerWallet);
              // Newest first
              return configurations.map(transformConfigurationFields).reverse();
            }
            case 'profitShareVaultV1': {
              // The Profit Share provider keeps only the current configuration
              // (no history accessor on its aggregator)
              const summary = await getProfitShareVaultProviderContract(
                vault.provider
              ).loadVaultSummary(managerWallet);
              return [
                transformProfitShareConfigurationFields(
                  summary.configurationFields
                ),
              ];
            }
            default:
              throw new UnreachableCaseError(vault);
          }
        },
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

export function useVaultOnChainPendingConfigurationQuery({
  managerWallet,
}: {
  managerWallet: string;
}) {
  const { data: vault } = useVaultQuery({ managerWallet });
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'pendingConfigurations',
      managerWallet,
      ...queryKeyForAggregator(vault),
    ],
    queryFn: !vault
      ? skipToken
      : async (): Promise<{
          blockTimestampThreshold: bigint;
          exists: boolean;
          vaultConfigurationFieldsTransformed:
            | VaultTransformedConfig
            | undefined;
        }> => {
          switch (vault.type) {
            case 'fixedIncomeVaultV1': {
              const response = await getFixedIncomeVaultProviderContract(
                vault.provider
              ).vaultConfigurationUpgradesByManagerWallet(managerWallet);
              return {
                blockTimestampThreshold: response.blockTimestampThreshold,
                exists: response.exists,
                vaultConfigurationFieldsTransformed: response.exists
                  ? transformConfigurationFields(
                      response.newVaultConfigurationFields
                    )
                  : undefined,
              };
            }
            case 'profitShareVaultV1': {
              const response = await getProfitShareVaultProviderContract(
                vault.provider
              ).vaultConfigurationUpgradesByManagerWallet(managerWallet);
              return {
                blockTimestampThreshold: response.blockTimestampThreshold,
                exists: response.exists,
                vaultConfigurationFieldsTransformed: response.exists
                  ? transformProfitShareConfigurationFields(
                      response.newVaultConfigurationFields
                    )
                  : undefined,
              };
            }
            default:
              throw new UnreachableCaseError(vault);
          }
        },
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

type VaultOnChainBalanceForWallet = {
  costBasis: bigint;
  initialDepositTimestampInS: bigint;
  pendingDepositQuantity: bigint;
  withdrawalLimitWindowEndTimestampInS: bigint;
  /** USDC value of the wallet's holding (Profit Share: shares at the current share price) */
  owedQuantity: bigint;
  /** USDC quantity locked by queued withdrawals */
  lockedQuantity: bigint;
  /** USDC quantity withdrawable now (rate limits and locked shares applied) */
  quantityAvailableToWithdraw: bigint;
  /** Profit Share only - share balances behind the USDC figures above */
  profitShare?: {
    sharePrice: bigint;
    totalShares: bigint;
    sharesAvailableToWithdraw: bigint;
    sharesLockedForWithdrawal: bigint;
  };
};

export function useVaultOnChainBalanceForWalletQuery({
  enabled,
  managerWallet,
  depositorWallet,
}: {
  enabled?: boolean;
  managerWallet: string;
  depositorWallet: string;
}) {
  const { data: vault } = useVaultQuery({ managerWallet });
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'loadVaultBalanceForWalletSummary',
      managerWallet,
      depositorWallet,
      ...queryKeyForAggregator(vault),
    ],
    // `enabled` must stay a separate option, not a skipToken condition - observers
    // share one query per key, so a skipToken from a disabled observer would remove
    // queryFn for all of them (causing "Missing queryFn" errors on invalidation).
    enabled,
    queryFn: !vault
      ? skipToken
      : async (): Promise<VaultOnChainBalanceForWallet> => {
          switch (vault.type) {
            case 'fixedIncomeVaultV1': {
              const balance = await getFixedIncomeVaultProviderContract(
                vault.provider
              ).loadVaultBalanceForWalletSummary(
                managerWallet,
                depositorWallet
              );
              if (typeof balance?.owedQuantity === 'undefined') {
                // Basic runtime structure check
                throw new Error(
                  'Incorrect loadVaultBalanceForWalletSummary response structure'
                );
              }
              return {
                costBasis: balance.costBasis,
                initialDepositTimestampInS: balance.initialDepositTimestampInS,
                lockedQuantity: balance.lockedQuantity,
                owedQuantity: balance.owedQuantity,
                pendingDepositQuantity: balance.pendingDepositQuantity,
                quantityAvailableToWithdraw:
                  balance.quantityAvailableToWithdraw,
                withdrawalLimitWindowEndTimestampInS:
                  balance.withdrawalLimitWindowEndTimestampInS,
              };
            }
            case 'profitShareVaultV1': {
              const contract = getProfitShareVaultProviderContract(
                vault.provider
              );
              const [balance, sharePrice] = await Promise.all([
                contract.loadVaultBalanceForWalletSummary(
                  managerWallet,
                  depositorWallet
                ),
                contract.loadSharePrice(managerWallet),
              ]);
              if (typeof balance?.totalShares === 'undefined') {
                throw new Error(
                  'Incorrect loadVaultBalanceForWalletSummary response structure'
                );
              }
              return {
                costBasis: balance.costBasis,
                initialDepositTimestampInS: balance.initialDepositTimestampInS,
                pendingDepositQuantity: balance.pendingDepositQuantity,
                withdrawalLimitWindowEndTimestampInS:
                  balance.withdrawalLimitWindowEndTimestampInS,
                owedQuantity: sharesToQuantityPips(
                  balance.totalShares,
                  sharePrice
                ),
                lockedQuantity: sharesToQuantityPips(
                  balance.sharesLockedForWithdrawal,
                  sharePrice
                ),
                quantityAvailableToWithdraw: sharesToQuantityPips(
                  balance.sharesAvailableToWithdraw,
                  sharePrice
                ),
                profitShare: {
                  sharePrice,
                  totalShares: balance.totalShares,
                  sharesAvailableToWithdraw: balance.sharesAvailableToWithdraw,
                  sharesLockedForWithdrawal: balance.sharesLockedForWithdrawal,
                },
              };
            }
            default:
              throw new UnreachableCaseError(vault);
          }
        },
    staleTime: seconds(7),
    refetchInterval: minutes(4),
  });
}

type VaultOnChainWithdrawalQueueItem = {
  nonce: bigint;
  depositorWallet: string;
  /** Requested USDC quantity (Profit Share: shares valued at the current share price) */
  grossQuantity: bigint;
  /** Profit Share only - the request is denominated in shares */
  profitShare?: {
    shares: bigint;
    /** Slippage protection of the request */
    minimumQuantity: bigint;
  };
};

export function useVaultOnChainWithdrawalQueueQuery({
  enabled,
  managerWallet,
  reduceInternalForProcessing,
}: {
  enabled?: boolean;
  managerWallet: string | undefined;
  reduceInternalForProcessing?: boolean;
}) {
  const { data: vault } = useVaultQuery({ managerWallet });
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'withdrawalQueue',
      managerWallet,
      ...queryKeyForAggregator(vault),
    ],
    // `enabled` must stay a separate option, not a skipToken condition (see
    // useVaultOnChainBalanceForWalletQuery)
    enabled,
    queryFn:
      !managerWallet || !vault || !vault.stateAggregator
        ? skipToken
        : async (): Promise<VaultOnChainWithdrawalQueueItem[]> => {
            switch (vault.type) {
              case 'fixedIncomeVaultV1': {
                const contractResponse =
                  await getFixedIncomeVaultAggregatorContract(
                    vault.stateAggregator
                  ).loadVaultWithdrawalQueue(managerWallet);
                return contractResponse.map(item => ({
                  depositorWallet: item.depositorWallet,
                  grossQuantity: item.grossQuantity,
                  nonce: item.nonce,
                }));
              }
              case 'profitShareVaultV1': {
                const aggregator = getProfitShareVaultAggregatorContract(
                  vault.stateAggregator
                );
                const [contractResponse, [sharePriceAndTotal]] =
                  await Promise.all([
                    aggregator.loadVaultWithdrawalQueue(managerWallet),
                    aggregator.loadSharePricesAndTotalSharesByVaults([
                      managerWallet,
                    ]),
                  ]);
                const sharePrice = sharePriceAndTotal?.sharePrice ?? 0n;
                return contractResponse.map(item => ({
                  depositorWallet: item.depositorWallet,
                  grossQuantity: sharesToQuantityPips(item.shares, sharePrice),
                  nonce: item.nonce,
                  profitShare: {
                    shares: item.shares,
                    minimumQuantity: item.minimumQuantity,
                  },
                }));
              }
              default:
                throw new UnreachableCaseError(vault);
            }
          },
    staleTime: seconds(15),
    refetchInterval: data =>
      data.state.data &&
      data.state.data?.length > 0 &&
      reduceInternalForProcessing
        ? seconds(11)
        : minutes(1),
  });
}

/** Depositor holding, normalized across providers */
type VaultOnChainDepositor = {
  costBasis: bigint;
  initialDepositTimestampInS: bigint;
  /** USDC value of the holding (Profit Share: `totalShares * sharePrice`) */
  owedQuantity: bigint;
  /** Profit Share only - share balance behind the USDC value */
  profitShare?: {
    totalShares: bigint;
    sharePrice: bigint;
  };
};

const toFixedIncomeDepositor = (depositor: {
  costBasis: bigint;
  initialDepositTimestampInS: bigint;
  owedQuantity: bigint;
}): VaultOnChainDepositor => ({
  costBasis: depositor.costBasis,
  initialDepositTimestampInS: depositor.initialDepositTimestampInS,
  owedQuantity: depositor.owedQuantity,
});

const toProfitShareDepositor = (depositor: {
  costBasis: bigint;
  initialDepositTimestampInS: bigint;
  sharePrice: bigint;
  totalShares: bigint;
}): VaultOnChainDepositor => ({
  costBasis: depositor.costBasis,
  initialDepositTimestampInS: depositor.initialDepositTimestampInS,
  owedQuantity: sharesToQuantityPips(
    depositor.totalShares,
    depositor.sharePrice
  ),
  profitShare: {
    totalShares: depositor.totalShares,
    sharePrice: depositor.sharePrice,
  },
});

/** Holdings of the given depositors in one vault, read from its aggregator */
async function loadDepositorsByManagerWallet(
  type: ApiVaultType,
  stateAggregator: string,
  managerWallet: string,
  depositors: string[]
): Promise<VaultOnChainDepositor[]> {
  switch (type) {
    case 'fixedIncomeVaultV1': {
      const list = await getFixedIncomeVaultAggregatorContract(
        stateAggregator
      ).loadVaultDepositorsByManagerWallet(managerWallet, depositors);
      return list.map(toFixedIncomeDepositor);
    }
    case 'profitShareVaultV1': {
      const list = await getProfitShareVaultAggregatorContract(
        stateAggregator
      ).loadVaultDepositorsByManagerWallet(managerWallet, depositors);
      return list.map(toProfitShareDepositor);
    }
    default:
      throw new UnreachableCaseError(type);
  }
}

/** Holdings of one depositor across the vaults served by an aggregator */
async function loadDepositorsByDepositorWallet(
  type: ApiVaultType,
  stateAggregator: string,
  managerWallets: string[],
  depositor: string
): Promise<(VaultOnChainDepositor | undefined)[]> {
  switch (type) {
    case 'fixedIncomeVaultV1': {
      const list = await getFixedIncomeVaultAggregatorContract(
        stateAggregator
      ).loadVaultDepositorsByDepositorWallet(managerWallets, depositor);
      return list.map(item =>
        item ? toFixedIncomeDepositor(item) : undefined
      );
    }
    case 'profitShareVaultV1': {
      const list = await getProfitShareVaultAggregatorContract(
        stateAggregator
      ).loadVaultDepositorsByDepositorWallet(managerWallets, depositor);
      return list.map(item =>
        item ? toProfitShareDepositor(item) : undefined
      );
    }
    default:
      throw new UnreachableCaseError(type);
  }
}

export function useVaultOnChainDepositorsMapQuery({
  managerWallet,
  depositors,
}: {
  managerWallet: string;
  depositors: string[] | undefined;
}) {
  const { data: vault } = useVaultQuery({ managerWallet });
  // Filter out disconnected wallet - empty string
  const depositorsFiltered = depositors?.filter(depositor => !!depositor);
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'depositors',
      managerWallet,
      depositorsFiltered,
      ...queryKeyForAggregator(vault),
    ],
    queryFn:
      !depositorsFiltered ||
      depositorsFiltered.length === 0 ||
      !vault ||
      !vault.stateAggregator
        ? skipToken
        : async () => {
            const responseList = await loadDepositorsByManagerWallet(
              vault.type,
              vault.stateAggregator,
              managerWallet,
              depositorsFiltered
            );
            return depositorsFiltered.reduce(
              (map, depositor, index) => {
                map[depositor] = responseList[index];
                return map;
              },
              {} as {
                [depositor: string]: VaultOnChainDepositor | undefined;
              }
            );
          },
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

export type OnChainVaultDepositorsMap = {
  [managerWallet: string]: VaultOnChainDepositor | undefined;
};

export function useVaultOnChainMulticallDepositorsMapQuery({
  managerVaults,
  depositor,
}: {
  /**
   * Aggregators differ per provider (and can differ per vault), so calls are
   * grouped per `stateAggregator` contract
   */
  managerVaults:
    | Pick<ApiVault, 'manager' | 'type' | 'stateAggregator'>[]
    | undefined;
  depositor: string;
}) {
  return useQuery<OnChainVaultDepositorsMap>({
    queryKey: ['vaultsOnChain', 'depositorMulticall', managerVaults, depositor],
    // Skip also without a depositor (no wallet connected) - both skip
    // dependencies are part of the queryKey
    queryFn:
      !managerVaults || !depositor
        ? skipToken
        : async () => {
            // Vaults without an aggregator address are left out of the map;
            // an aggregator serves one provider type
            const managersByAggregator = new Map<
              string,
              { type: ApiVaultType; managers: string[] }
            >();
            for (const { manager, type, stateAggregator } of managerVaults) {
              if (stateAggregator) {
                const group = managersByAggregator.get(stateAggregator);
                if (group) {
                  group.managers.push(manager);
                } else {
                  managersByAggregator.set(stateAggregator, {
                    type,
                    managers: [manager],
                  });
                }
              }
            }
            const depositorsMap: OnChainVaultDepositorsMap = {};
            await Promise.all(
              [...managersByAggregator.entries()].map(
                async ([
                  stateAggregator,
                  { type, managers: managerWallets },
                ]) => {
                  const list = await loadDepositorsByDepositorWallet(
                    type,
                    stateAggregator,
                    managerWallets,
                    depositor
                  );
                  managerWallets.forEach((managerWallet, index) => {
                    depositorsMap[managerWallet] = list[index];
                  });
                }
              )
            );
            return depositorsMap;
          },
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

type VaultOnChainSummaryBase = {
  isDepositEnabled: boolean;
  isExited: boolean;
  isLiquidated: boolean;
  exitedAccountValue: bigint;
  exitedAtTimestampInS: bigint;
  /** Net depositor deposits (deposits - withdrawals), compared against the deposit cap */
  depositorNetDeposits: bigint;
  numDepositorWallets: bigint;
};

type FixedIncomeVaultOnChainSummary = VaultOnChainSummaryBase & {
  type: 'fixedIncomeVaultV1';
  didManagerWalletWithdrawExit: boolean;
  exitedTotalOwedQuantity: bigint;
  managerLockedQuantity: bigint;
  totalLockedQuantity: bigint;
  /** Total value owed to depositors incl. accrued interest */
  totalOwedQuantity: bigint;
  /** Transformed chain data with already converted to Decimal from PIP */
  configurationFieldsTransformed: FixedIncomeVaultTransformedConfig;
};

type ProfitShareVaultOnChainSummary = VaultOnChainSummaryBase & {
  type: 'profitShareVaultV1';
  exitedPendingDepositQuantity: bigint;
  exitedTotalShares: bigint;
  depositorPendingDepositQuantity: bigint;
  /** Current share price in USDC (pips) */
  sharePrice: bigint;
  /** Total share supply (pips) */
  totalShares: bigint;
  configurationFieldsTransformed: ProfitShareVaultTransformedConfig;
};

type VaultOnChainSummary =
  | FixedIncomeVaultOnChainSummary
  | ProfitShareVaultOnChainSummary;

export function useVaultOnChainSummaryQuery({
  managerWallet,
}: {
  managerWallet: string;
}) {
  const { data: vault } = useVaultQuery({ managerWallet });
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'summary',
      managerWallet,
      ...queryKeyForAggregator(vault),
    ],
    queryFn: !vault
      ? skipToken
      : async (): Promise<VaultOnChainSummary> => {
          // Note: we cannot use spread as the ethers Result would not be serializable
          switch (vault.type) {
            case 'fixedIncomeVaultV1': {
              const response = await getFixedIncomeVaultProviderContract(
                vault.provider
              ).loadVaultSummary(managerWallet);
              return {
                type: 'fixedIncomeVaultV1',
                didManagerWalletWithdrawExit:
                  response.didManagerWalletWithdrawExit,
                exitedAccountValue: response.exitedAccountValue,
                exitedAtTimestampInS: response.exitedAtTimestampInS,
                exitedTotalOwedQuantity: response.exitedTotalOwedQuantity,
                isDepositEnabled: response.isDepositEnabled,
                isExited: response.isExited,
                isLiquidated: response.isLiquidated,
                managerLockedQuantity: response.managerLockedQuantity,
                depositorNetDeposits: response.depositorNetDeposits,
                numDepositorWallets: response.numDepositorWallets,
                totalLockedQuantity: response.totalLockedQuantity,
                totalOwedQuantity: response.totalOwedQuantity,
                configurationFieldsTransformed: transformConfigurationFields(
                  response.configurationFields
                ),
              };
            }
            case 'profitShareVaultV1': {
              const response = await getProfitShareVaultProviderContract(
                vault.provider
              ).loadVaultSummary(managerWallet);
              return {
                type: 'profitShareVaultV1',
                isDepositEnabled: response.isDepositEnabled,
                isExited: response.isExited,
                isLiquidated: response.isLiquidated,
                exitedAccountValue: response.exitedAccountValue,
                exitedAtTimestampInS: response.exitedAtTimestampInS,
                exitedPendingDepositQuantity:
                  response.exitedPendingDepositQuantity,
                exitedTotalShares: response.exitedTotalShares,
                depositorNetDeposits: response.depositorNetDeposits,
                depositorPendingDepositQuantity:
                  response.depositorPendingDepositQuantity,
                numDepositorWallets: response.numDepositorWallets,
                sharePrice: response.sharePrice,
                totalShares: response.totalShares,
                configurationFieldsTransformed:
                  transformProfitShareConfigurationFields(
                    response.configurationFields
                  ),
              };
            }
            default:
              throw new UnreachableCaseError(vault);
          }
        },
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

export function useVaultOnChainQuantityAvailableForExitWithdrawalQuery({
  enabled,
  managerWallet,
}: {
  enabled?: boolean;
  managerWallet: string | undefined;
}) {
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'quantityAvailableForExitWithdrawalData',
      managerWallet,
    ],
    // `enabled` must stay a separate option, not a skipToken condition (see
    // useVaultOnChainBalanceForWalletQuery)
    enabled,
    queryFn: !managerWallet
      ? skipToken
      : async () => {
          const quantityBN =
            await getExchangeContract().loadQuoteQuantityAvailableForExitWithdrawal(
              managerWallet
            );
          const quantityInDecimal = pipToDecimal(quantityBN);
          return { quantityInDecimal };
        },
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

export function useVaultOnChainExitWalletSimulationQuery({
  enabled,
  managerWallet,
  depositorWallet,
}: {
  enabled?: boolean;
  managerWallet: string;
  /** Simulated msg.sender - only depositors may call exitWallet */
  depositorWallet: string | undefined;
}) {
  const { data: vault } = useVaultQuery({ managerWallet });
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'exitWalletSimulation',
      managerWallet,
      depositorWallet,
      ...queryKeyForAggregator(vault),
    ],
    enabled,
    queryFn:
      !depositorWallet || !vault
        ? skipToken
        : async () => {
            try {
              // eth_call dry-run of the real exit transaction - success means
              // the contract would accept exitWallet from this depositor now
              await getVaultProviderContract(vault).exitWallet.staticCall(
                managerWallet,
                { from: depositorWallet }
              );
              return true;
            } catch (error) {
              if (isCallException(error)) {
                console.log(
                  '[vaultsOnChainQueries:ExitWalletSimulation] not eligible',
                  error
                );
                // Contract revert (e.g. WalletCannotBeExited) - not eligible
                return false;
              }
              // RPC/network failure - keep the query in error state so
              // eligibility stays unknown instead of reading as "not eligible"
              throw error;
            }
          },
    staleTime: seconds(15),
    // The queue-age eligibility condition unlocks purely by time passing, so
    // re-simulate every minute to catch the flip
    refetchInterval: minutes(1),
  });
}

/**
 * Exchange-level exit status of the vault (manager) wallet. `exists` flips once
 * `exitWallet` is mined; the contracts accept Withdraw Exit only from
 * `effectiveTimestampInMs` on (exit time + chain propagation period).
 */
export function useVaultOnChainWalletExitStatusQuery({
  enabled,
  managerWallet,
}: {
  enabled?: boolean;
  managerWallet: string;
}) {
  return useQuery({
    queryKey: ['vaultsOnChain', 'walletExitStatus', managerWallet],
    enabled,
    queryFn: async () => {
      const exitStatus = await getExchangeContract().loadWalletExitStatus(
        managerWallet
      );
      return {
        exists: exitStatus.exists,
        effectiveTimestampInMs:
          Number(exitStatus.effectiveBlockTimestamp) * 1000,
      };
    },
    staleTime: seconds(15),
    // Availability unlocks purely by time passing
    refetchInterval: minutes(2),
  });
}
